import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, Copy, Check, Mail, Truck, Clock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { firePurchaseOnce, getCheckoutEventId, clearCheckoutEventId } from "@/lib/metaPixel";
import { fireGoogleAdsPurchase } from "@/lib/googleAds";
import { track } from "@/lib/pixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;
const POLL_INTERVAL_MS = 2500;
const REPEAT_CODE = "DUBAI15";

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;
        setData(res.data);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          localStorage.removeItem("dp_discount");

          // Expose verified purchase data as a global JS object so that
          // Google Ads' URL-based conversion (with JS-variable extraction)
          // can pick up the transaction_id, value and currency reliably.
          // Only set AFTER backend confirms payment_status === 'paid'.
          // Real Stripe-confirmed amount (in major units, e.g. dollars).
          // We round to exactly 2 decimals so Pixel + CAPI send the same
          // number string and Meta's deduplication / EMQ score is perfect.
          const verifiedValue = Math.round(Number(res.data.amount_total || 0) * 100) / 100;
          const verifiedCurrency = (res.data.currency || "usd").toUpperCase();
          window.dpPurchase = {
            transaction_id: sessionId,
            value: verifiedValue,
            currency: verifiedCurrency,
            email: res.data.customer_email || null,
          };
          // Append ?paid=1 to the URL (after confirmed paid) so Google Ads
          // URL-based conversion only fires on verified purchases — never
          // on cancelled/expired/spoofed /success visits.
          try {
            const u = new URL(window.location.href);
            if (!u.searchParams.get("paid")) {
              u.searchParams.set("paid", "1");
              window.history.replaceState({}, "", u.toString());
            }
          } catch {
            /* no-op */
          }

          // Meta Pixel: fire Purchase exactly once per session_id.
          // event_id matches the one we generated at InitiateCheckout so the
          // server-side CAPI Purchase (from the Stripe webhook) dedupes.
          // Skip silently if value didn't resolve > 0 (prevents the
          // "all Purchases same price" Events Manager warning).
          const eventId = getCheckoutEventId();
          if (eventId && verifiedValue > 0) {
            firePurchaseOnce(sessionId, eventId, {
              value: verifiedValue,
              currency: verifiedCurrency,
              content_ids: [`DP-CLASSIC-${res.data.qty || 1}`],
              content_type: "product",
              contents: [{
                id: `DP-CLASSIC-${res.data.qty || 1}`,
                quantity: res.data.qty || 1,
                item_price: Math.round((verifiedValue / Math.max(res.data.qty || 1, 1)) * 100) / 100,
              }],
              num_items: res.data.qty || 1,
              order_id: sessionId,
            });
            // Allow the next purchase flow to start fresh
            clearCheckoutEventId();
          }

          // Google Ads: fire Purchase conversion (idempotent via transaction_id in localStorage).
          // Skips silently if REACT_APP_GOOGLE_ADS_LABEL env var is empty.
          // Safe to keep alongside the URL-based detection — Google dedupes
          // by transaction_id within the same Conversion ID.
          fireGoogleAdsPurchase({
            transactionId: sessionId,
            value: verifiedValue,
            currency: verifiedCurrency,
            email: res.data.customer_email || undefined,
          });

          // Internal Events feed — record the purchase exactly once per
          // session so it surfaces in the Admin Live Events stream in green.
          try {
            const purchaseKey = `dp_internal_purchase_logged_${sessionId}`;
            if (!localStorage.getItem(purchaseKey)) {
              track("purchase", {
                session_id: sessionId,
                value: verifiedValue,
                currency: verifiedCurrency,
                qty: res.data.qty || 1,
                bundle: `DP-CLASSIC-${res.data.qty || 1}`,
              });
              localStorage.setItem(purchaseKey, "1");
            }
          } catch {
            /* no-op */
          }

          return;
        }
        if (res.data.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_POLLS) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(REPEAT_CODE);
      setCopied(true);
      toast.success(`${REPEAT_CODE} copied to clipboard`);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.success(`Code: ${REPEAT_CODE}`);
    }
  };

  const reshopWithDiscount = async () => {
    try {
      await navigator.clipboard.writeText(REPEAT_CODE);
    } catch {
      /* clipboard unavailable — non-blocking */
    }
    localStorage.setItem("dp_discount", REPEAT_CODE);
    toast.success(`${REPEAT_CODE} applied · 15% off at checkout`);
    window.location.href = "/#shop";
  };

  return (
    <div className="min-h-screen bg-[#fafaf9] aurora-wrap flex items-center justify-center px-6 py-16">
      <Seo
        title="Thank you! · Order confirmed · Dubai Pillows"
        description="Your luxury Dubai Pillow is being prepared. Track your order and enjoy 15% off your next purchase."
        url={`${(process.env.REACT_APP_SITE_URL || "").replace(/\/$/, "")}/success`}
        noindex={true}
      />
      <div className="max-w-3xl w-full bg-white rounded-[32px] border border-black/5 shadow-xl p-8 md:p-12">
        {status === "checking" && (
          <div className="text-center">
            <Loader2 className="mx-auto animate-spin text-[#0e0e10]" size={42} />
            <h1 className="text-[28px] md:text-[34px] font-black text-[#0e0e10] mt-6 tracking-tight">
              Confirming your order…
            </h1>
            <p className="mt-3 text-[#6b6b72] text-[14px]">
              Hang tight — we&apos;re verifying your payment securely.
            </p>
          </div>
        )}

        {status === "paid" && (
          <>
            <div className="text-center">
              <div className="sticker sticker-lime inline-block text-[12px] mb-4">
                Payment confirmed
              </div>
              <CheckCircle2 className="mx-auto text-[#22c55e]" size={56} />
              <h1 className="text-[28px] md:text-[40px] font-black text-[#0e0e10] mt-5 tracking-tight leading-tight">
                Thank you. You&apos;re in.
              </h1>
              <p className="mt-3 text-[#5a5a60] text-[15px] max-w-lg mx-auto">
                Your order is now being prepared by our team in Dubai. You&apos;ll find every step below.
              </p>
            </div>

            {/* ORDER SUMMARY */}
            {data && (
              <div
                data-testid="order-breakdown"
                className="mt-8 bg-[#fafaf9] rounded-2xl p-5 md:p-6 text-left text-[13px] border border-black/5"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-[#8a8a8a]">
                    Order summary
                  </div>
                  {sessionId && (
                    <div
                      data-testid="success-order-id"
                      className="text-[11px] text-[#8a8a8a] font-mono"
                      title="Save this Order ID to track your order"
                    >
                      ID: {sessionId.slice(-12)}
                    </div>
                  )}
                </div>
                {data.qty ? (
                  <div className="space-y-2">
                    <div className="flex justify-between gap-3">
                      <span className="text-[#3a3a40]">
                        Dubai Pillow{data.qty > 1 ? "s" : ""}
                        <span className="text-[#8a8a8a] ml-1.5">
                          · {data.height || "Medium"} loft · {data.color || "Classic White"}
                        </span>
                      </span>
                      <span className="font-bold text-[#0e0e10] tabular-nums">×{data.qty}</span>
                    </div>
                    {Array.isArray(data.gift_mask_colors) && data.gift_mask_colors.length > 0 && (
                      <div className="flex justify-between gap-3 text-[#22c55e]">
                        <span>🎁 3D Sleep Mask <span className="text-[#3a3a40]/70">({data.gift_mask_colors.join(", ")})</span></span>
                        <span className="font-bold">FREE GIFT</span>
                      </div>
                    )}
                    <div className="border-t border-black/5 my-2" />
                    <div className="flex justify-between gap-3">
                      <span className="text-[#3a3a40]">Free worldwide shipping</span>
                      <span className="font-bold text-[#22c55e]">Included</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-[#3a3a40]">100-night sleep trial</span>
                      <span className="font-bold text-[#22c55e]">Included</span>
                    </div>
                    <div className="border-t border-black/5 my-2" />
                    <div className="flex justify-between gap-3 text-[15px]">
                      <span className="font-bold text-[#0e0e10]">Total paid</span>
                      <span className="font-black text-[#0e0e10] tabular-nums">
                        ${data.amount_total?.toFixed(2)} {data.currency?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between gap-3 text-[15px]">
                    <span className="font-bold text-[#0e0e10]">Total paid</span>
                    <span className="font-black text-[#0e0e10] tabular-nums">
                      ${data.amount_total?.toFixed(2)} {data.currency?.toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* PROCESSING TIMELINE */}
            <div
              data-testid="processing-timeline"
              className="mt-8 rounded-2xl border border-black/5 p-5 md:p-6 bg-white"
            >
              <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-[#8a8a8a] mb-4">
                What happens next
              </div>
              <ol className="space-y-4 text-[13px] md:text-[14px]">
                <li className="flex gap-3.5">
                  <span className="shrink-0 w-9 h-9 rounded-full bg-[#22c55e]/15 text-[#22c55e] inline-flex items-center justify-center">
                    <CheckCircle2 size={18} />
                  </span>
                  <div>
                    <div className="font-bold text-[#0e0e10]">Order received</div>
                    <div className="text-[#6b6b72] mt-0.5">Payment confirmed. A confirmation email is on its way to your inbox.</div>
                  </div>
                </li>
                <li className="flex gap-3.5">
                  <span className="shrink-0 w-9 h-9 rounded-full bg-[#fff4d6] text-[#b8952a] inline-flex items-center justify-center">
                    <Clock size={18} />
                  </span>
                  <div>
                    <div className="font-bold text-[#0e0e10]">Processing — 24-48 hours</div>
                    <div className="text-[#6b6b72] mt-0.5">
                      Hand-stitched & quality-checked in our Dubai warehouse. Most orders are dispatched the next business day.
                    </div>
                  </div>
                </li>
                <li className="flex gap-3.5">
                  <span className="shrink-0 w-9 h-9 rounded-full bg-[#0e0e10]/8 text-[#0e0e10] inline-flex items-center justify-center">
                    <Truck size={18} />
                  </span>
                  <div>
                    <div className="font-bold text-[#0e0e10]">Tracking number by email</div>
                    <div className="text-[#6b6b72] mt-0.5">
                      Within 48h we&apos;ll email your live tracking link — follow your order from our warehouse to your doorstep, anywhere in the world.
                    </div>
                  </div>
                </li>
                <li className="flex gap-3.5">
                  <span className="shrink-0 w-9 h-9 rounded-full bg-[#d8f06b]/30 text-[#0e0e10] inline-flex items-center justify-center">
                    <Mail size={18} />
                  </span>
                  <div>
                    <div className="font-bold text-[#0e0e10]">Delivered in 5-7 business days</div>
                    <div className="text-[#6b6b72] mt-0.5">
                      Express international shipping included. Then start your 100-night sleep trial — risk-free.
                    </div>
                  </div>
                </li>
              </ol>
            </div>

            {/* TRUST RIBBON */}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] text-[#5a5a60]">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} className="text-[#22c55e]" /> Stripe Verified Payment</span>
              <span className="inline-flex items-center gap-1.5"><Truck size={14} /> DHL / FedEx Express</span>
              <span className="inline-flex items-center gap-1.5"><Mail size={14} /> support@dubaipillows.com</span>
            </div>

            {/* DUBAI15 REPEAT-PURCHASE COUPON */}
            <div
              data-testid="thankyou-coupon"
              className="mt-8 rounded-3xl p-6 md:p-7 bg-gradient-to-br from-[#fff8d6] via-[#fdf4a8] to-[#d8f06b] text-[#0e0e10] relative overflow-hidden"
            >
              <div className="absolute -top-8 -right-8 w-40 h-40 bg-white/30 rounded-full blur-2xl" />
              <div className="relative">
                <div className="text-[10px] uppercase tracking-[0.22em] font-extrabold text-[#0e0e10]/70">
                  A gift from us to you
                </div>
                <h3 className="mt-2 text-[24px] md:text-[30px] font-black leading-tight">
                  Loved your pillow? Get 15% off your next order.
                </h3>
                <p className="mt-2.5 text-[13px] md:text-[14px] text-[#0e0e10]/75 max-w-lg">
                  Add a pillow for a partner, treat a family member, or stock the guest room. Copy your code and use it at checkout — valid on any bundle.
                </p>

                <div className="mt-5 flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={copyCode}
                    data-testid="thankyou-coupon-copy"
                    className={`flex-1 inline-flex items-center justify-between gap-3 rounded-full px-5 py-3.5 font-extrabold transition ${
                      copied
                        ? "bg-[#22c55e] text-white"
                        : "bg-[#0e0e10] text-white hover:bg-white hover:text-[#0e0e10]"
                    }`}
                  >
                    <span className="text-[16px] md:text-[18px] tabular-nums tracking-[0.18em]">
                      {REPEAT_CODE}
                    </span>
                    <span className="text-[11px] uppercase tracking-wider inline-flex items-center gap-1.5">
                      {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Tap to copy</>}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={reshopWithDiscount}
                    data-testid="thankyou-coupon-shop"
                    className="flex-1 inline-flex items-center justify-center rounded-full bg-white text-[#0e0e10] px-5 py-3.5 font-extrabold text-[13px] uppercase tracking-wider border-2 border-[#0e0e10] hover:bg-[#0e0e10] hover:text-white transition"
                  >
                    Shop with 15% off →
                  </button>
                </div>
                <p className="mt-3 text-[11px] text-[#0e0e10]/55">
                  One-time use per customer · auto-applies at Stripe checkout · stacks with launch pricing.
                </p>
              </div>
            </div>

            <div className="mt-8 text-center">
              <Link
                to="/"
                data-testid="success-back-home"
                className="text-[12px] uppercase tracking-[0.2em] text-[#8a8a8a] hover:text-[#0e0e10] underline"
              >
                Return to homepage
              </Link>
            </div>
          </>
        )}

        {(status === "expired" || status === "error" || status === "timeout") && (
          <div className="text-center">
            <XCircle className="mx-auto text-[#ef4444]" size={56} />
            <h1 className="text-[28px] md:text-[32px] font-black text-[#0e0e10] mt-5">
              {status === "expired" ? "Session expired" : "Could not confirm"}
            </h1>
            <p className="mt-3 text-[#6b6b72] text-[14px]">
              {status === "timeout"
                ? "Still processing. Check your email shortly — we'll send confirmation as soon as Stripe finalizes."
                : "Please try again or contact support@dubaipillows.com if you were charged."}
            </p>
            <Link to="/" className="pill-btn inline-flex mt-8 px-7 py-3.5 text-[14px]">
              Return to shop →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
